import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import type { JourneyStepId } from './incidentDrawerJourneyProgress';

type JourneyStep = { id: JourneyStepId; title: string };

export type IncidentDrawerStepNavigationProps = {
  stepId: JourneyStepId;
  journeyStepIds: JourneyStepId[];
  journeySteps: JourneyStep[];
  completedJourneySteps: Partial<Record<JourneyStepId, boolean>>;
  journeyStateMeta: (stepId: JourneyStepId) => { label: string; color: string };
  onToggleJourneyStep: (stepId: JourneyStepId) => void;
  onScrollToJourneyStep: (stepId: JourneyStepId) => void;
};

export const IncidentDrawerStepNavigation: React.FC<IncidentDrawerStepNavigationProps> = ({
  stepId,
  journeyStepIds,
  journeySteps,
  completedJourneySteps,
  journeyStateMeta,
  onToggleJourneyStep,
  onScrollToJourneyStep,
}) => {
  const index = journeyStepIds.indexOf(stepId);
  if (index === -1) return null;
  const currentStep = journeySteps[index];
  const previousStep = index > 0 ? journeySteps[index - 1] : null;
  const nextStep = index < journeySteps.length - 1 ? journeySteps[index + 1] : null;
  const isCompleted = Boolean(completedJourneySteps[stepId]);
  const nextDisabled = Boolean(nextStep) && !isCompleted;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: -0.5, mb: 2 }}>
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 800, color: journeyStateMeta(stepId).color, display: 'block' }}>
          {journeyStateMeta(stepId).label}
        </Typography>
        {!isCompleted && nextStep ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15 }}>
            Mark this step done before moving the workflow forward.
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant={isCompleted ? 'contained' : 'outlined'}
          color={isCompleted ? 'success' : 'primary'}
          aria-label={isCompleted ? `Completed ${currentStep.title}` : `Mark ${currentStep.title} done`}
          onClick={() => onToggleJourneyStep(stepId)}
        >
          {isCompleted ? 'Completed' : 'Mark step done'}
        </Button>
        {previousStep ? (
          <Button size="small" variant="text" onClick={() => onScrollToJourneyStep(previousStep.id)}>
            Back
          </Button>
        ) : null}
        {nextStep ? (
          <Button
            size="small"
            variant="outlined"
            disabled={nextDisabled}
            onClick={() => onScrollToJourneyStep(nextStep.id)}
          >
            {`Next step: ${nextStep.title}`}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};

export default IncidentDrawerStepNavigation;
